import { Injectable } from '@angular/core';
import { CacheService } from './cache.service';
import { OwnedContactsApiService } from './api';
import { UserService } from '../../core/services/user.service';
import { debounceTime, filter, map, shareReplay, startWith, switchMap, take, tap } from 'rxjs/operators';
import { User } from '../../core/models/user.model';
import { BehaviorSubject, combineLatest, forkJoin, iif, Observable, of } from 'rxjs';
import {
    OwnedContactDto,
    OwnedContactsOptionSettingsRequestDto,
    OwnedContactsOptionSettingsResponseDto,
    OwnedContactsOwnerFieldsDto,
    OwnedContactsStatusDto,
    OwnedContactsVendorType,
} from '../dto';
import { isEqual, orderBy, startCase, toLower } from 'lodash-es';
import { LoadingService } from './loading.service';
import { CrmOwnedContactsStatusEnum, FeatureFlagsEnum } from '../enums';
import { FeatureFlagService } from './feature-flag.service';

export const OWNED_CONTACTS_LOADING_TAG: string = 'owned-contacts-loading-tag';
const OWNED_CONTACTS_CACHE_TAG: string = 'owned-contacts-contacts';
const OWNED_ACCOUNTS_CACHE_TAG: string = 'owned-contacts-accounts';

@Injectable({
    providedIn: 'root',
})
export class OwnedContactsService {
    private readonly ownedContactsOptionSettingsSubject: BehaviorSubject<OwnedContactsOptionSettingsResponseDto[]> = new BehaviorSubject(null);
    private readonly ownedContactsStatusSubject: BehaviorSubject<OwnedContactsStatusDto[]> = new BehaviorSubject(null);
    private readonly ownedContactsByIdSubject: BehaviorSubject<Record<string, OwnedContactDto>> = new BehaviorSubject(null);
    private readonly ownedContactsIdsToUpdateSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>(null);
    private readonly activeCrmOwnedContacts$: Observable<OwnedContactsOptionSettingsResponseDto> = this.getActiveOwnedContactsOptionSettings()
    .pipe(debounceTime(100), shareReplay(1));

    private readonly refreshSubject: BehaviorSubject<symbol> = new BehaviorSubject<symbol>(null);

    constructor(
        private readonly cacheService: CacheService,
        private readonly ownedContactsApiService: OwnedContactsApiService,
        private readonly userService: UserService,
        private readonly loadingService: LoadingService,
        private readonly featureFlagService: FeatureFlagService,
    ) {
        this.listenToOwnedContactsIdsToUpdate();
        this.listenToRefresh();
    }

    get ownedContactsById$(): Observable<Record<string, OwnedContactDto>> {
        return this.ownedContactsByIdSubject.asObservable();
    }

    addOwnedContactsIdsToUpdate(ids: string[]) {
        const value: string[] = this.ownedContactsIdsToUpdateSubject.getValue();
        const newValue: string[] = Array.from(new Set([...(value || []), ...ids]));

        if (!isEqual(value, newValue)) {
            this.ownedContactsIdsToUpdateSubject.next(newValue);
        }
    }

    saveOwnedContactsOptionSettings(settings: OwnedContactsOptionSettingsRequestDto): Observable<OwnedContactsOptionSettingsResponseDto> {
        return this.loadingService.loadingWrapper(this.ownedContactsApiService.saveOwnedContactsOptionSettings(settings), OWNED_CONTACTS_LOADING_TAG)
            .pipe(tap(() => this.refreshOwnedContactsAndStatus()));
    }

    getActiveOwnedContactsOptionSettings(): Observable<OwnedContactsOptionSettingsResponseDto> {
        return this.ownedContactsOptionSettingsSubject.asObservable().pipe(
            tap((settings: OwnedContactsOptionSettingsResponseDto[]) => this.tapRefreshHandler(settings)),
            map((settings: OwnedContactsOptionSettingsResponseDto[]) => this.getActiveSetting(settings))
        );
    }

    getCrmOwnedContactsHidden(startWithTrue: boolean = true): Observable<boolean> {
        return this.featureFlagService.getFlag(FeatureFlagsEnum.CrmOwnedContacts).pipe(
          switchMap((flag: boolean) => {
              if (!flag) { return of(!flag); }

              const source$: Observable<boolean> = this.activeCrmOwnedContacts$.pipe(map((res: OwnedContactsOptionSettingsResponseDto) => !res));

              return startWithTrue ? source$.pipe(startWith(true)) : source$;
          }),
        );
    }

    getCrmOwnedContactsDisabled(startWithTrue: boolean = true): Observable<boolean> {
        return this.activeCrmOwnedContacts$.pipe(
          filter(Boolean),
          startWith(startWithTrue ? true : null),
          switchMap((res: OwnedContactsOptionSettingsResponseDto) => this.getOwnedContactsStatus(res.vendor).pipe(take(1))),
          map((res: OwnedContactsStatusDto) => !res?.sync_status_complete),
        );
    }

    getOwnedContactsOptionSettings(vendor: OwnedContactsVendorType = 'SALESFORCE'): Observable<OwnedContactsOptionSettingsResponseDto> {
        return this.ownedContactsOptionSettingsSubject.asObservable().pipe(
            tap((settings: OwnedContactsOptionSettingsResponseDto[]) => this.tapRefreshHandler(settings)),
            map((settings: OwnedContactsOptionSettingsResponseDto[]) => settings?.find((o: OwnedContactsOptionSettingsResponseDto) => o.vendor === vendor)),
        );
    }

    getOwnedContactsStatus(vendor: OwnedContactsVendorType = 'SALESFORCE'): Observable<OwnedContactsStatusDto> {
        return this.ownedContactsStatusSubject.asObservable().pipe(
            tap((settings: OwnedContactsStatusDto[]) => this.tapRefreshHandler(settings)),
            map((settings: OwnedContactsStatusDto[]) => settings?.find((o: OwnedContactsStatusDto) => o.vendor === vendor)),
        );
    }

    refreshOwnedContactsAndStatus(): void {
        this.refreshSubject.next(Symbol());
        this.cacheService.delete(OWNED_CONTACTS_CACHE_TAG);
        this.cacheService.delete(OWNED_ACCOUNTS_CACHE_TAG);
    }

    getOwnedContactsContactsCacheable(includeUnassigned: boolean = true): Observable<OwnedContactDto[]> {
        const stream$: Observable<[User, OwnedContactsOptionSettingsResponseDto]> = this.getUserAndActiveSettingStream();

        return this.loadingService.loadingWrapper(
            this.cacheService.getCachedObservable(OWNED_CONTACTS_CACHE_TAG, stream$.pipe(
                take(1),
                switchMap(([{ account_id }, settings]: [User, OwnedContactsOptionSettingsResponseDto]) => {
                    if (!settings?.vendor) {
                        return of([]);
                    }

                    return this.ownedContactsApiService.getOwnedContacts(account_id, 'contact', settings.vendor).pipe(
                        map((response: OwnedContactDto[]) => this.getMappedData(response, includeUnassigned)),
                        tap((response: OwnedContactDto[]) => {
                            this.addOwnedContactsById(response);
                        }),
                    );
                }),
            )), OWNED_CONTACTS_LOADING_TAG,
        );
    }

    getOwnedContactsAccountsCacheable(includeUnassigned: boolean = true): Observable<OwnedContactDto[]> {
        const stream$: Observable<[User, OwnedContactsOptionSettingsResponseDto]> = this.getUserAndActiveSettingStream();

        return this.loadingService.loadingWrapper(
            this.cacheService.getCachedObservable(OWNED_ACCOUNTS_CACHE_TAG, stream$.pipe(
                take(1),
                switchMap(([{ account_id }, settings]: [User, OwnedContactsOptionSettingsResponseDto]) => {
                    if (!settings?.vendor) {
                        return of([]);
                    }

                    return this.ownedContactsApiService.getOwnedContacts(account_id, 'account', settings.vendor).pipe(
                        map((response: OwnedContactDto[]) => this.getMappedData(response, includeUnassigned)),
                        tap((response: OwnedContactDto[]) => {
                            this.addOwnedContactsById(response);
                        }),
                    );
                }),
            )), OWNED_CONTACTS_LOADING_TAG,
        );
    }

    getOwnedContactsAccounts(includeUnassigned: boolean = true): Observable<OwnedContactDto[]> {
        const stream$: Observable<[User, OwnedContactsOptionSettingsResponseDto]> = this.getUserAndActiveSettingStream();

        return stream$.pipe(
          take(1),
          switchMap(([{ account_id }, settings]: [User, OwnedContactsOptionSettingsResponseDto]) => {
              if (!settings?.vendor) {
                  return of([]);
              }

              return this.ownedContactsApiService.getOwnedContacts(account_id, 'account', settings.vendor).pipe(
                  map((response: OwnedContactDto[]) => this.getMappedData(response, includeUnassigned)),
              );
          }),
        );
    }

    getOwnedMappingFields(vendor: OwnedContactsVendorType): Observable<OwnedContactsOwnerFieldsDto> {
        return combineLatest([this.userService.getUserData(), this.refreshSubject.asObservable()]).pipe(
          switchMap(([{ account_id }]: [User, symbol]) => this.loadingService.loadingWrapper(this.ownedContactsApiService.getOwnerFields(account_id, vendor), OWNED_CONTACTS_LOADING_TAG)),
          map(({ _id, ...rest}: OwnedContactsOwnerFieldsDto) => rest),
        );
    }

    private getUserAndActiveSettingStream(): Observable<[User, OwnedContactsOptionSettingsResponseDto]> {
        return combineLatest<[User, OwnedContactsOptionSettingsResponseDto]>([
            this.userService.getUserData().pipe(filter(Boolean), take(1)),
            this.getActiveOwnedContactsOptionSettings().pipe(filter((value: OwnedContactsOptionSettingsResponseDto) => value !== null), take(1)),
        ]);
    }

    private getRecordsById(response: OwnedContactDto[]): Record<string, OwnedContactDto> {
        return response.reduce((acc: Record<string, OwnedContactDto>, item: OwnedContactDto) => ({
            ...acc,
            [item.crmUserId]: item,
        }), {});
    }

    private getMappedData(response: OwnedContactDto[], includeUnassigned: boolean = true): OwnedContactDto[] {
        const activeSetting: OwnedContactsOptionSettingsResponseDto = this.getActiveSetting(this.ownedContactsOptionSettingsSubject.getValue());
        const orderedList: OwnedContactDto[] = orderBy(response, [res => res.crmFirstname?.toLowerCase(), res => res.crmLastname?.toLowerCase()]);

        return (includeUnassigned ? [{
            crmEmail: 'unassigned',
            crmUserId: 'unassigned',
            crmFirstname: 'Unassigned in',
            crmLastname: startCase(toLower(activeSetting?.vendor)),
        }, ...orderedList] : orderedList).filter(Boolean);
    }

    private addOwnedContactsById(response: OwnedContactDto[]): void {
        const value: Record<string, OwnedContactDto> = this.ownedContactsByIdSubject.getValue();
        this.ownedContactsByIdSubject.next({
            ...value,
            ...this.getRecordsById(response),
        });
    }

    private getOwnedContactsByIds(account_id: number, ids: string[]) {
        return this.loadingService.loadingWrapper(this.ownedContactsApiService.getOwnedContactsByIds(account_id, ids), OWNED_CONTACTS_LOADING_TAG);
    }

    private listenToOwnedContactsIdsToUpdate(): void {
        combineLatest([
            this.userService.getUserData().pipe(filter(Boolean), map(({ account_id }: User) => account_id)),
            this.ownedContactsIdsToUpdateSubject.asObservable().pipe(debounceTime(100), filter(Boolean)),
        ]).pipe(
            switchMap(([account_id, ids]: [number, string[]]) => this.getOwnedContactsByIds(account_id, ids)),
        ).subscribe((res: OwnedContactDto[]) => {
            this.addOwnedContactsById(res);
            this.ownedContactsIdsToUpdateSubject.next(null);
        });
    }

    private listenToRefresh(): void {
        this.refreshSubject.pipe(
            filter(Boolean),
            debounceTime(100),
            switchMap(() => this.userService.getUserData().pipe(filter(Boolean), take(1))),
            switchMap(({ account_id }: User) => this.loadingService.loadingWrapper(forkJoin([
                this.ownedContactsApiService.getOwnedContactsOptionSettingsV2(account_id),
                this.ownedContactsApiService.getOwnedContactsStatusV2(account_id),
            ]), OWNED_CONTACTS_LOADING_TAG)),
        ).subscribe(([settings, status]: [OwnedContactsOptionSettingsResponseDto[], OwnedContactsStatusDto[]]) => {
            this.ownedContactsOptionSettingsSubject.next(settings);
            this.ownedContactsStatusSubject.next(status);
        });
    }

    private getActiveSetting(settings: OwnedContactsOptionSettingsResponseDto[]) {
        return settings?.find((o: OwnedContactsOptionSettingsResponseDto) => o.status === CrmOwnedContactsStatusEnum.Active);
    }

    private tapRefreshHandler<T>(value: T): void {
        if (!value) {
            this.refreshOwnedContactsAndStatus();
        }
    }
}
